import React, { useCallback, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { WalletControls } from '../wallet/WalletControls';
import { ClusterSwitcher } from '../wallet/ClusterSwitcher';
import { MobileMenu } from './MobileMenu';
import { soundManager } from '../../utils/sounds';
import {
  prefetchMatchmakingData,
  prefetchLeaderboardData,
  prefetchPlayerStats,
} from '../../utils/prefetch';
import { useCloseableMatches } from '../../api/matches';

const links = [
  { to: '/', label: 'HOME' },
  { to: '/matchmaking', label: 'MATCH' },
  { to: '/info', label: 'INFO' },
  { to: '/faq', label: 'FAQ' },
  { to: '/practice', label: 'PRACTICE' },
  { to: '/tutorial', label: 'TUTORIAL' },
  { to: '/seasons', label: 'REWARDS' },
  { to: '/staking', label: 'STAKE' },
  { to: '/friends', label: 'SOCIAL' },
  { to: '/wallet', label: 'WALLET' },
  { to: '/swap', label: 'SWAP' },
  { to: '/settings', label: 'SETTINGS' },
];

export const NavBar: React.FC = () => {
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toBase58() ?? null;
  const { data: closeableMatches } = useCloseableMatches(walletAddress);
  const hasReclaimableRent = (closeableMatches?.length ?? 0) > 0;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Prefetch data on link hover for instant page loads
  const handleLinkHover = useCallback(
    (to: string) => {
      soundManager.play('click');
      // Prefetch relevant data based on the route
      switch (to) {
        case '/matchmaking':
          prefetchMatchmakingData(walletAddress);
          break;
        case '/leaderboard':
          prefetchLeaderboardData();
          break;
        case '/settings':
        case '/wallet':
          prefetchPlayerStats(walletAddress);
          break;
      }
    },
    [walletAddress],
  );

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/90 backdrop-blur-md pt-safe">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:max-w-none sm:py-2">
        <NavLink to="/" className="flex items-center gap-3 no-underline sm:shrink-0 sm:gap-2">
          <img
            src="/solante-logo.png"
            alt="Solante"
            width="36"
            height="36"
            className="h-9 w-9 sm:h-8 sm:w-8"
            style={{ imageRendering: 'pixelated' }}
          />
          <div>
            <p className="font-display text-base font-semibold text-arcade-cyan sm:text-lg">
              SOLANTE
            </p>
            <p className="font-pixel text-[10px] tracking-wide text-arcade-pink/80">
              SOLANA LORE • REAL STAKES
            </p>
          </div>
        </NavLink>
        <nav className="ml-4 hidden items-center sm:flex">
          {links.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => soundManager.play('select')}
              onMouseEnter={() => handleLinkHover(to)}
              className={({ isActive }) =>
                `pixel-corners whitespace-nowrap px-1.5 lg:px-2 py-1.5 font-display text-[10px] lg:text-[11px] transition-all duration-100 relative ${
                  isActive
                    ? 'bg-arcade-cyan text-black'
                    : 'text-arcade-cyan/80 hover:bg-arcade-cyan/10 hover:text-arcade-cyan'
                }`
              }
            >
              {label}
              {to === '/wallet' && hasReclaimableRent && (
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-600 opacity-75" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-rose-600 border border-rose-400" />
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="flex sm:shrink-0 items-center gap-2">
          {/* Desktop: show cluster switcher (localhost only) and wallet controls */}
          <div className="hidden sm:flex items-center gap-2">
            {(window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1') && <ClusterSwitcher />}
            <WalletControls />
          </div>

          {/* Mobile: show compact wallet button and hamburger */}
          <div className="flex sm:hidden items-center gap-2">
            <WalletControls />
            <button
              onClick={() => {
                soundManager.play('click');
                setMobileMenuOpen(true);
              }}
              className="flex h-11 w-11 items-center justify-center rounded-lg border border-arcade-cyan/30 bg-black/40 text-arcade-cyan transition-colors hover:bg-arcade-cyan/10"
              aria-label="Open menu"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} links={links} />
    </header>
  );
};
